import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Send,
  Sparkles,
  Mail,
  Check,
  Loader2,
  Bike,
  Car,
  Truck,
  Anchor,
  Music2,
  MapPin,
  Heart,
  Users,
  Play,
} from "lucide-react";

// ============================================================
// NOW CASTING — dedicated single-page landing intended for
// casting.automotoville.com. Mobile-first, cinematic, IG/TikTok
// link-in-bio optimized. All Apply Now buttons deep-link into
// the existing intake system on `/` via `?apply=<action>` (or
// direct routes for shows with dedicated pages).
// ============================================================

const LOGO =
  "https://customer-assets.emergentagent.com/job_98369023-526e-447a-9536-c9b52a5bb5cf/artifacts/mnyeefvz_99DD8AA5-D993-42EB-AA6D-F95FD03FDFF8.png";

const SHOWS = [
  {
    id: "love-in-the-fast-lane",
    title: "Love in the Fast Lane",
    line: "A dating reality experience built for the culture.",
    who: "Bikers, truckers, car lovers, boaters, builders & riders who actually live the lifestyle.",
    poster:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/tstxxcvb_63366FCD-3430-4F13-B40B-E09872280990.png",
    applyAction: "love-in-the-fast-lane",
    cta: "Shoot Your Shot",
    accent: "#FF2D9C",
  },
  {
    id: "culture-files",
    title: "Culture Files",
    line: "Everybody has a story — not everybody tells it.",
    who: "Real people with real experiences in trucking, biking, cars, boating, or any culture lane.",
    poster:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/rry9s4g2_copy_38C57C12-14AB-42C0-9C65-BB7D859309F5.jpeg",
    applyAction: "culture-files",
    cta: "Submit Your Story",
    accent: "#00E5FF",
  },
  {
    id: "we-outside",
    title: "We Outside",
    line: "If it's lit… we pull up.",
    who: "Promoters & hosts of MC annuals, car meets, truck shows, boat parties, ride-outs & takeovers.",
    poster:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/d2395beg_A5884D80-C4CF-4EC1-9EEB-ACBDA8777EA8.png",
    applyAction: "events",
    cta: "Submit Your Event",
    accent: "#FF2D9C",
  },
  {
    id: "stage-for-the-streets",
    title: "Stage for the Streets",
    line: "Raw talent. Real opportunity. The streets pick who's next.",
    who: "Rappers, R&B singers, DJs, dancers, spoken word, MCs, live bands — all hungry talent welcome.",
    poster:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/xubdvo1j_117F3023-0ADA-49D2-869F-0F10D3E4BDF0.png",
    applyAction: "performers",
    cta: "Submit Performance",
    accent: "#00E5FF",
  },
  {
    id: "built-not-bought",
    title: "Built Not Bought",
    line: "The builders. The garages. The ones who do it themselves.",
    who: "Custom builders, fabricators, garages & visionaries on motorcycles, cars, trucks & beyond.",
    poster:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/3v2xh8sr_copy_D0ADCA47-6C49-46C0-91CB-90516C8F432F.jpeg",
    applyAction: "built-not-bought",
    cta: "Submit Your Build",
    accent: "#FF2D9C",
  },
  {
    id: "the-set-speaks",
    title: "The Set Speaks",
    line: "Every patch has a perspective. The set is finally speaking.",
    who: "MC & SC club members — officers, OGs, biker wives, and women of the set.",
    poster:
      "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/fsq1ep9o_102609F8-756D-4BDE-920D-A04F846FDB7E.png",
    applyHref: "/the-set-speaks",
    cta: "Apply Now",
    accent: "#00E5FF",
  },
];

const COMMUNITIES = [
  { label: "Motorcycle", Icon: Bike },
  { label: "Cars", Icon: Car },
  { label: "Trucking", Icon: Truck },
  { label: "Boating", Icon: Anchor },
  { label: "Music", Icon: Music2 },
  { label: "Street", Icon: Users },
  { label: "Travel", Icon: MapPin },
  { label: "Dating", Icon: Heart },
];

export default function NowCasting() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const apply = (item) => {
    if (item.applyHref) {
      navigate(item.applyHref);
      return;
    }
    if (item.applyAction) {
      navigate(`/?apply=${encodeURIComponent(item.applyAction)}`);
      return;
    }
    navigate("/");
  };

  const scrollToShows = () =>
    document
      .getElementById("casting-shows")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div
      className="relative min-h-screen bg-[#05050a] text-white overflow-x-hidden"
      data-testid="now-casting-page"
    >
      <Hero
        onApply={() => apply({ applyAction: "culture-files" })}
        onViewShows={scrollToShows}
      />
      <ShowsGrid shows={SHOWS} onApply={apply} />
      <WhyApply />
      <FounderBlock />
      <EmailCapture />
      <Footer />
    </div>
  );
}

// ----------------------------------------------------------------
// 1. Hero
// ----------------------------------------------------------------
function Hero({ onApply, onViewShows }) {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-[100svh] w-full overflow-hidden flex flex-col items-center justify-center px-5 py-16">
      {/* Animated neon spotlights */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0.3 }}
        animate={{ opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background:
            "radial-gradient(circle at 20% 25%, rgba(255,45,156,0.35), transparent 45%), radial-gradient(circle at 80% 75%, rgba(0,229,255,0.30), transparent 50%)",
        }}
        aria-hidden
      />
      {/* Subtle grain */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
        aria-hidden
      />

      {/* Back to AMV chip */}
      <button
        type="button"
        onClick={() => navigate("/")}
        data-testid="now-casting-back"
        className="absolute top-5 left-5 z-20 inline-flex items-center gap-2 px-3 py-2 border border-white/15 bg-black/40 backdrop-blur-sm text-white/85 font-accent text-[10px] tracking-[0.28em] hover:bg-white/10 transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> AUTOMOTOVILLE
      </button>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 max-w-3xl mx-auto text-center"
      >
        <img
          src={LOGO}
          alt="AutoMotoVille"
          className="h-16 sm:h-20 mx-auto drop-shadow-[0_0_24px_rgba(255,45,156,0.45)]"
        />
        <div className="mt-7 font-accent text-[11px] sm:text-xs tracking-[0.4em] text-[#00E5FF]">
          / CASTING · {new Date().getFullYear()}
        </div>
        <h1
          className="mt-3 font-heading uppercase font-black leading-[0.9] tracking-tight text-6xl sm:text-7xl md:text-8xl"
          style={{
            color: "#F6EBD4",
            textShadow:
              "0 6px 40px rgba(0,0,0,0.6), 0 0 1px rgba(255,255,255,0.5)",
          }}
        >
          Now
          <br />
          <span
            style={{
              background:
                "linear-gradient(90deg, #FF2D9C 0%, #00E5FF 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Casting
          </span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-md mx-auto">
          <span className="text-white font-semibold">Real people.</span>{" "}
          <span className="text-white font-semibold">Real stories.</span>{" "}
          <span className="text-white font-semibold">Real culture.</span>
        </p>
        <p className="mt-3 text-sm text-white/55 leading-relaxed max-w-sm mx-auto">
          AutoMotoVille is casting across motorcycles, cars, trucks, boating,
          music, street, travel & dating — all on one platform built for the
          culture.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-sm sm:max-w-none mx-auto">
          <button
            type="button"
            onClick={onApply}
            data-testid="now-casting-hero-apply"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF2D9C] to-[#00E5FF] text-white font-black px-7 py-4 uppercase tracking-[0.28em] text-xs shadow-[0_12px_36px_-12px_rgba(255,45,156,0.75)] hover:scale-[1.03] active:scale-[0.97] transition-transform"
          >
            <Send className="w-4 h-4" /> Apply Now
          </button>
          <button
            type="button"
            onClick={onViewShows}
            data-testid="now-casting-hero-view-shows"
            className="inline-flex items-center justify-center gap-2 border border-white/40 text-white font-black px-7 py-4 uppercase tracking-[0.28em] text-xs hover:bg-white/5 active:scale-[0.97] transition-all"
          >
            <Play className="w-4 h-4" /> View Shows
          </button>
        </div>

        {/* IG/TikTok handle hint */}
        <div className="mt-10 font-accent text-[10px] tracking-[0.4em] text-white/40">
          @AUTOMOTOVILLE · CASTING.AUTOMOTOVILLE.COM
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 6, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        aria-hidden
      >
        <div className="w-px h-10 bg-gradient-to-b from-transparent via-white/40 to-transparent" />
      </motion.div>
    </section>
  );
}

// ----------------------------------------------------------------
// 2 + 3. Featured Casting Shows
// ----------------------------------------------------------------
function ShowsGrid({ shows, onApply }) {
  return (
    <section
      id="casting-shows"
      className="relative px-5 sm:px-8 py-20 md:py-24"
    >
      <div className="max-w-6xl mx-auto">
        <div className="font-accent text-[11px] sm:text-xs tracking-[0.32em] text-[#00E5FF]">
          / FEATURED · NOW CASTING
        </div>
        <h2
          className="mt-3 font-heading uppercase font-black leading-[1.02] tracking-tight text-3xl sm:text-4xl md:text-5xl"
          style={{ color: "#F6EBD4" }}
        >
          Pick your lane.
        </h2>
        <p className="mt-3 max-w-xl text-sm sm:text-base text-white/65 leading-relaxed">
          Six original shows. One culture. Tap any card to apply — no
          experience needed, only a point of view.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {shows.map((s, idx) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: idx * 0.05 }}
              className="group relative bg-[#0c0a10]/85 backdrop-blur-sm border border-white/10 overflow-hidden hover:border-white/25 transition-colors flex flex-col"
              data-testid={`now-casting-show-${s.id}`}
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-black">
                <div
                  className="absolute inset-0 bg-cover bg-center scale-110 blur-xl opacity-60"
                  style={{ backgroundImage: `url(${s.poster})` }}
                  aria-hidden
                />
                <img
                  src={s.poster}
                  alt={s.title}
                  loading="lazy"
                  className="relative w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                <span
                  className="absolute top-3 left-3 font-accent text-[10px] tracking-[0.28em] px-2.5 py-1 backdrop-blur-md border"
                  style={{
                    color: s.accent,
                    borderColor: `${s.accent}66`,
                    background: `${s.accent}1A`,
                  }}
                >
                  NOW CASTING
                </span>
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ boxShadow: `inset 0 0 60px ${s.accent}55` }}
                />
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3
                  className="font-heading font-black uppercase tracking-tight text-xl leading-tight"
                  style={{ color: "#F6EBD4" }}
                >
                  {s.title}
                </h3>
                <p className="mt-1.5 text-sm text-white/70 leading-snug">
                  {s.line}
                </p>
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="font-accent text-[10px] tracking-[0.28em] text-white/45 mb-1">
                    WHO SHOULD APPLY
                  </div>
                  <p className="text-xs text-white/60 leading-relaxed">
                    {s.who}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onApply(s)}
                  data-testid={`now-casting-apply-${s.id}`}
                  className="mt-5 inline-flex items-center justify-between gap-2 px-4 py-3 font-heading font-black text-[11px] tracking-[0.22em] uppercase text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    background: `linear-gradient(90deg, ${s.accent}, ${
                      s.accent === "#FF2D9C" ? "#00E5FF" : "#FF2D9C"
                    })`,
                    boxShadow: `0 10px 26px -12px ${s.accent}99`,
                  }}
                >
                  <span>{s.cta}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// 4. Why Apply
// ----------------------------------------------------------------
function WhyApply() {
  return (
    <section className="relative px-5 sm:px-8 py-20 md:py-24 bg-[#08070c]">
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 25%, rgba(255,45,156,0.18), transparent 50%), radial-gradient(circle at 85% 75%, rgba(0,229,255,0.18), transparent 55%)",
        }}
        aria-hidden
      />
      <div className="relative max-w-5xl mx-auto">
        <div className="font-accent text-[11px] sm:text-xs tracking-[0.32em] text-[#FF2D9C]">
          / WHY APPLY
        </div>
        <h2
          className="mt-3 font-heading uppercase font-black leading-[1.02] tracking-tight text-3xl sm:text-4xl md:text-5xl"
          style={{ color: "#F6EBD4" }}
        >
          This is for the culture.
        </h2>
        <p className="mt-4 max-w-2xl text-sm sm:text-base text-white/65 leading-relaxed">
          AutoMotoVille is the only network casting across every lane of
          real-life culture — at the same time, on one platform, with real
          producers watching every tape.
        </p>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {COMMUNITIES.map(({ label, Icon }, idx) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.35, delay: idx * 0.04 }}
              className="group relative bg-[#0c0a10]/70 border border-white/10 px-4 py-5 flex flex-col items-center gap-2 hover:border-white/25 transition-colors text-center"
              data-testid={`why-apply-${label.toLowerCase()}`}
            >
              <span className="inline-flex w-10 h-10 rounded-full items-center justify-center bg-gradient-to-br from-[#FF2D9C] to-[#00E5FF] shadow-[0_0_24px_rgba(255,45,156,0.4)]">
                <Icon className="w-4 h-4 text-white" />
              </span>
              <div className="font-heading font-black uppercase text-xs sm:text-sm tracking-tight text-white">
                {label}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Bullet
            kicker="REAL STAGE"
            body="Stories from our community — not from a focus group."
          />
          <Bullet
            kicker="REAL EYES"
            body="Every tape gets reviewed by an actual AMV producer."
          />
          <Bullet
            kicker="REAL OWNERSHIP"
            body="A founder-led network building space for the culture, not on top of it."
          />
        </div>
      </div>
    </section>
  );
}

function Bullet({ kicker, body }) {
  return (
    <div className="bg-[#0c0a10]/60 border border-white/10 p-5">
      <div className="font-accent text-[10px] tracking-[0.32em] text-[#00E5FF]">
        / {kicker}
      </div>
      <p className="mt-2 text-sm text-white/75 leading-snug">{body}</p>
    </div>
  );
}

// ----------------------------------------------------------------
// 5. Founder block — Evelina "Boss Lady"
// ----------------------------------------------------------------
function FounderBlock() {
  return (
    <section className="relative px-5 sm:px-8 py-20 md:py-28 overflow-hidden">
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 80% 20%, rgba(255,45,156,0.18), transparent 45%), radial-gradient(circle at 20% 80%, rgba(0,229,255,0.18), transparent 50%)",
        }}
        aria-hidden
      />
      <div className="relative max-w-5xl mx-auto grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-center">
        {/* Portrait card */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55 }}
          className="relative mx-auto md:mx-0 w-full max-w-xs aspect-[4/5] bg-[#0c0a10] border border-white/10 overflow-hidden"
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,45,156,0.25), transparent 55%), linear-gradient(315deg, rgba(0,229,255,0.25), transparent 55%)",
            }}
            aria-hidden
          />
          <div className="absolute inset-0 flex items-end p-5">
            <div className="text-white">
              <div className="font-accent text-[10px] tracking-[0.32em] text-[#FF2D9C]">
                / EXECUTIVE PRODUCER · NETWORK OWNER
              </div>
              <div
                className="mt-1 font-heading font-black uppercase leading-tight text-2xl sm:text-3xl"
                style={{ color: "#F6EBD4" }}
              >
                Evelina
                <br />
                <span
                  style={{
                    background:
                      "linear-gradient(90deg, #FF2D9C, #00E5FF)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  "Boss Lady"
                </span>
              </div>
            </div>
          </div>
          {/* Neon corner glows */}
          <div
            className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-40 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(255,45,156,0.6), transparent 65%)",
            }}
            aria-hidden
          />
          <div
            className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full opacity-30 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(0,229,255,0.6), transparent 65%)",
            }}
            aria-hidden
          />
        </motion.div>

        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55, delay: 0.05 }}
        >
          <div className="font-accent text-[11px] sm:text-xs tracking-[0.32em] text-[#FF2D9C]">
            / FOUNDER'S NOTE
          </div>
          <h2
            className="mt-3 font-heading uppercase font-black leading-[1.02] tracking-tight text-3xl sm:text-4xl md:text-5xl"
            style={{ color: "#F6EBD4" }}
          >
            Built by the culture.
            <br />
            <span
              style={{
                background:
                  "linear-gradient(90deg, #FF2D9C, #00E5FF)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              For the culture.
            </span>
          </h2>
          <p className="mt-5 text-sm sm:text-base text-white/75 leading-relaxed max-w-xl">
            I started AutoMotoVille because the stories I lived — and the
            people I came up with — were never the ones getting picked up.
            We're not waiting for a green light. We're building the network
            ourselves, on our terms.
          </p>
          <p className="mt-4 text-sm sm:text-base text-white/65 leading-relaxed max-w-xl">
            If you've got a story, a build, a stage, an event, or a lane —
            this is your shot. Apply once and we'll match you to the show
            that fits.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 px-3 py-2 border border-white/15 bg-white/[0.03] font-accent text-[10px] tracking-[0.28em] text-white/85">
            <Sparkles className="w-3.5 h-3.5 text-[#00E5FF]" /> — EVELINA "BOSS
            LADY", AMV NETWORK
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// 6. Email capture
// ----------------------------------------------------------------
function EmailCapture() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v || !v.includes("@")) {
      setError("Enter a valid email.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: v }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("success");
    } catch {
      setError("Something went wrong. Try again.");
      setStatus("error");
    }
  };

  return (
    <section className="relative px-5 sm:px-8 py-20 md:py-24 bg-[#08070c]">
      <div className="relative max-w-2xl mx-auto text-center">
        <div className="font-accent text-[11px] sm:text-xs tracking-[0.32em] text-[#00E5FF]">
          / CASTING UPDATES
        </div>
        <h2
          className="mt-3 font-heading uppercase font-black leading-[1.02] tracking-tight text-3xl sm:text-4xl md:text-5xl"
          style={{ color: "#F6EBD4" }}
        >
          Get the calls first.
        </h2>
        <p className="mt-3 text-sm sm:text-base text-white/65 leading-relaxed max-w-md mx-auto">
          New shows. New casting drops. New cities. Drop your email — we only
          send when there's a real opportunity.
        </p>

        {status === "success" ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="now-casting-email-success"
            className="mt-8 inline-flex items-center gap-2 px-5 py-3 border border-[#00E5FF]/40 bg-[#00E5FF]/[0.08] text-[#00E5FF] font-heading font-black text-xs tracking-[0.22em] uppercase"
          >
            <Check className="w-4 h-4" /> You're on the list
          </motion.div>
        ) : (
          <form
            onSubmit={submit}
            noValidate
            className="mt-8 flex flex-col sm:flex-row gap-2 sm:gap-3 max-w-md mx-auto"
            data-testid="now-casting-email-form"
          >
            <div className="relative flex-1">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                disabled={status === "sending"}
                data-testid="now-casting-email-input"
                autoComplete="email"
                className="w-full bg-black/40 border border-white/15 pl-9 pr-3 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="now-casting-email-submit"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF2D9C] to-[#00E5FF] disabled:opacity-70 px-6 py-3 font-heading font-black text-xs tracking-[0.22em] uppercase text-white shadow-[0_10px_30px_-12px_rgba(255,45,156,0.7)] hover:scale-[1.03] active:scale-[0.97] transition-transform"
            >
              {status === "sending" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending
                </>
              ) : (
                <>
                  Notify Me <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </form>
        )}
        {status === "error" && error && (
          <p
            data-testid="now-casting-email-error"
            className="mt-3 text-xs text-[#FF6FA8]"
          >
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// 7. Footer
// ----------------------------------------------------------------
function Footer() {
  return (
    <footer
      className="relative px-5 sm:px-8 pt-14 pb-10 text-center"
      data-testid="now-casting-footer"
    >
      <img
        src={LOGO}
        alt="AutoMotoVille"
        className="h-10 mx-auto opacity-90"
      />
      <p className="mt-4 text-xs text-white/55 max-w-md mx-auto leading-relaxed">
        AutoMotoVille is a culture-driven entertainment network casting across
        motorcycles, cars, trucking, boating, music, street, travel, dating
        & beyond.
      </p>
      <a
        href="mailto:casting@automotoville.com"
        data-testid="now-casting-footer-email"
        className="mt-5 inline-flex items-center gap-2 px-3 py-2 border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] font-accent text-[10px] tracking-[0.28em] text-white/80 transition-colors"
      >
        <Mail className="w-3.5 h-3.5 text-[#FF2D9C]" />
        CASTING@AUTOMOTOVILLE.COM
      </a>
      <div className="mt-8 font-accent text-[10px] tracking-[0.32em] text-white/30">
        AUTOMOTOVILLE · {new Date().getFullYear()} · REAL CULTURE. REAL
        STORIES. REAL ENTERTAINMENT.
      </div>
    </footer>
  );
}
